import { ObservationMedia } from '../../types/api';
import { apiClient } from './client';

const BASE_URL = '/uploads';

export const uploadsApi = {
  /**
   * Upload media file for an observation
   */
  async uploadObservationMedia(
    observationId: string, 
    file: File, 
    caption?: string
  ): Promise<ObservationMedia> {
    const formData = new FormData();
    formData.append('file', file);
    if (caption) {
      formData.append('caption', caption);
    }

    const response = await apiClient.post(
      `${BASE_URL}/observation-media/${observationId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * Delete observation media
   */
  async deleteObservationMedia(mediaId: string): Promise<{ message: string }> {
    const response = await apiClient.delete(`${BASE_URL}/observation-media/${mediaId}`);
    return response.data;
  },

  /**
   * Get observation media details
   */
  async getObservationMedia(mediaId: string): Promise<ObservationMedia> {
    const response = await apiClient.get(`${BASE_URL}/observation-media/${mediaId}`);
    return response.data;
  },

  /**
   * Update observation media caption
   */
  async updateObservationMedia(mediaId: string, caption: string): Promise<ObservationMedia> {
    const response = await apiClient.put(`${BASE_URL}/observation-media/${mediaId}`, {
      caption,
    });
    return response.data;
  },

  /**
   * Get all media for an observation
   */
  async getObservationMediaList(observationId: string): Promise<ObservationMedia[]> {
    const response = await apiClient.get(`${BASE_URL}/observation/${observationId}/media`);
    return response.data;
  },

  /**
   * Upload profile photo for current user
   */
  async uploadProfilePhoto(file: File): Promise<{
    message: string;
    url: string;
    thumbnail_url: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post(
      `${BASE_URL}/profile-photo`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * Delete profile photo for current user
   */
  async deleteProfilePhoto(): Promise<{ message: string }> {
    const response = await apiClient.delete(`${BASE_URL}/profile-photo`);
    return response.data;
  },

  /**
   * Validate file before upload
   */
  validateObservationMedia(file: File): { isValid: boolean; error?: string } {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'video/mp4',
      'video/quicktime',
      'video/x-msvideo',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];

    if (file.size > maxSize) {
      return {
        isValid: false,
        error: 'File size must be less than 10MB',
      };
    }

    if (!allowedTypes.includes(file.type)) {
      return {
        isValid: false,
        error: 'File type not supported. Please use images, videos, or documents.',
      };
    }

    return { isValid: true };
  },

  /**
   * Validate profile photo before upload
   */
  validateProfilePhoto(file: File): { isValid: boolean; error?: string } {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
    ];

    if (file.size > maxSize) {
      return {
        isValid: false,
        error: 'Image size must be less than 5MB',
      };
    }

    if (!allowedTypes.includes(file.type)) {
      return {
        isValid: false,
        error: 'Only image files are allowed for profile photos',
      };
    }

    return { isValid: true };
  },

  /**
   * Get media type from file
   */
  getMediaType(file: File): 'image' | 'video' | 'document' {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('video/')) return 'video';
    return 'document';
  },

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },
};
